import { useRef, useState, useEffect, useCallback } from "react";
import type { CapturedSignature, SignaturePoint } from "@aam/shared";

interface SignatureCanvasProps {
  value: string; // base64 data URL
  onChange: (value: string) => void;
  /**
   * The raw stroke paths behind the image. Carrier portals (Pinnacle's Konva
   * pad) record pointer paths rather than images, so the agent's signature has
   * to be re-drawn on them; keeping the paths lets that be an exact replay
   * instead of a trace recovered from pixels. Undefined once the signature is
   * cleared, or when it was typed rather than drawn — typing renders text with
   * fillText and produces no strokes at all.
   */
  onStrokesChange?: (captured: CapturedSignature | undefined) => void;
  id?: string;
  describedBy?: string;
  invalid?: boolean;
}

export function SignatureCanvas({
  value,
  onChange,
  onStrokesChange,
  id,
  describedBy,
  invalid,
}: SignatureCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(!value);
  const [typedSignature, setTypedSignature] = useState("");
  const lastPos = useRef<{ x: number; y: number } | null>(null);
  const strokes = useRef<SignaturePoint[][]>([]);

  // Re-draw saved signature when value changes externally (e.g. on form resume)
  useEffect(() => {
    if (value && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.onload = () => {
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
        ctx?.drawImage(img, 0, 0);
      };
      img.src = value;
      setIsEmpty(false);
    }
  }, []); // Only on mount

  const getPos = (
    e: React.MouseEvent | React.TouchEvent,
    canvas: HTMLCanvasElement
  ): { x: number; y: number } => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ("touches" in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    setDrawing(true);
    lastPos.current = getPos(e, canvas);
    // A fresh pen-down starts a new stroke, so a signature written in pieces
    // replays as pieces rather than one line joining them.
    strokes.current.push([{ ...lastPos.current }]);

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(lastPos.current.x, lastPos.current.y);
    }
  };

  const draw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      if (!drawing || !canvasRef.current || !lastPos.current) return;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const pos = getPos(e, canvas);
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = getComputedStyle(document.documentElement).getPropertyValue("--content-primary").trim() || "#22231f";

      ctx.beginPath();
      ctx.moveTo(lastPos.current.x, lastPos.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();

      lastPos.current = pos;
      strokes.current[strokes.current.length - 1]?.push({ x: pos.x, y: pos.y });
      setIsEmpty(false);
    },
    [drawing]
  );

  const endDraw = useCallback(() => {
    if (!drawing) return;
    setDrawing(false);
    lastPos.current = null;
    // A tap that never moved leaves a one-point stroke that draws nothing.
    strokes.current = strokes.current.filter((stroke) => stroke.length >= 2);
    if (canvasRef.current) {
      onChange(canvasRef.current.toDataURL("image/png"));
      onStrokesChange?.(
        strokes.current.length
          ? {
              width: canvasRef.current.width,
              height: canvasRef.current.height,
              strokes: strokes.current.map((stroke) => stroke.map((point) => ({ ...point }))),
            }
          : undefined
      );
    }
  }, [drawing, onChange, onStrokesChange]);

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    strokes.current = [];
    onChange("");
    onStrokesChange?.(undefined);
    setIsEmpty(true);
    setTypedSignature("");
  };

  const applyTypedSignature = (name: string) => {
    setTypedSignature(name);
    // Typed signatures are rendered with fillText — there are no strokes to
    // replay, so downstream has to recover a path from the image instead.
    strokes.current = [];
    onStrokesChange?.(undefined);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!name.trim()) {
      onChange("");
      setIsEmpty(true);
      return;
    }
    ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue("--content-primary").trim() || "#22231f";
    ctx.font = "italic 48px Georgia, serif";
    ctx.textBaseline = "middle";
    ctx.fillText(name.trim(), 36, canvas.height / 2, canvas.width - 72);
    setIsEmpty(false);
    onChange(canvas.toDataURL("image/png"));
  };

  return (
    <div className="space-y-2">
      <div className="relative rounded-xl overflow-hidden border-2 border-border-default bg-surface-input transition-colors focus-within:border-accent">
        <canvas
          ref={canvasRef}
          width={800}
          height={200}
          className="w-full touch-none cursor-crosshair"
          aria-label="Draw your signature"
          aria-describedby={describedBy}
          aria-invalid={invalid}
          style={{ height: "140px" }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-content-muted text-sm select-none">Sign here</p>
          </div>
        )}
        {/* Signature baseline */}
        <div className="absolute bottom-8 left-6 right-6 border-b border-border-default pointer-events-none" />
      </div>

      <div>
        <label htmlFor={id} className="block text-xs font-medium text-content-secondary">
          Keyboard alternative: type your legal name
        </label>
        <input
          id={id}
          type="text"
          value={typedSignature}
          onChange={(event) => applyTypedSignature(event.target.value)}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          className="mt-1 w-full rounded-xl border border-border-default bg-surface-input px-4 py-2.5 text-content-primary"
          autoComplete="name"
        />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-content-muted">Draw with a pointer or type your name</p>
        {!isEmpty && (
          <button
            type="button"
            onClick={clear}
            className="min-h-11 px-3 text-xs text-content-muted hover:text-status-rejected-text transition-colors font-medium"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
